import WhatsAppButton from './elements/WhatsAppButton';
import { ApiRequest } from './GlobalFunctions';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { ErrorBoundary } from 'elements/ErrorBoundary';

import Home from './components/Home/Home';
import Login from './components/Auth/Login';
import AccountKinesiologists from 'components/Account/AccountKinesiologists';
import AccountUsers from 'components/Account/AccountUsers';
import AccountEditor from 'components/Account/AccountEditor';
import KinesiologistsList from 'components/Kinesiologists/KinesiologistsList';
import KinesiologistDetail from 'components/Kinesiologists/KinesiologistDetail';
import Terms from 'Pages/Terms';
import BannersList from 'components/Admin/Banners/BannersList';

import GlobalBlog from 'components/Blog/GlobalBlog';

import Users from 'components/Admin/Users/UsersList';
import BlogList from 'components/Admin/Blog/BlogList';
import WhatsappLogsList from 'components/Admin/WhatsappLogs/WhatsappLogsList';
import NursesReport from 'components/Admin/Nurses/NursesReport';
import RedirectsList from 'components/Admin/Redirects/RedirectsList';
import AboutUs from 'Pages/AboutUs';
import BlogDetails from 'components/Blog/BlogDetails';
import Settings from 'components/Admin/Settings/Settings';
import BadgesList from 'components/Admin/Badges/BadgesList';
import CompaniesList from 'components/Admin/Companies/CompaniesList';
import CompanyDetails from 'components/Companies/CompanyDetails';
import AdminRatingsList from 'components/Admin/Ratings/RatingsList';
import FAQ from 'components/FAQ/FAQ';

interface ModuleResponse {
  module: string;
  id?: string | number;
}

const componentMap: Record<string, any> = {
  'blog': BlogDetails,
  'kinesiologists': KinesiologistDetail
  // Puedes agregar más módulos aquí
};

/**
 * AppRoutes — definición de rutas de la aplicación.
 *
 * Usa `useAuth()` para determinar el rol del usuario en lugar de leer
 * localStorage directamente, garantizando reactividad al login/logout.
 *
 * Bug corregido: la dependencia del useEffect era `window.location.pathname`
 * (no reactivo); ahora usa `location.pathname` via `useLocation()`.
 */
export default function AppRoutes(props: any) {
  const { role_id } = useAuth();
  const { setLoading } = props;
  const location = useLocation();

  const [moduleData, setModuleData] = useState<ModuleResponse | null>(null);
  // checkedPath: última URL cuya resolución de alias terminó.
  // Si location.pathname !== checkedPath el resolver está en vuelo → mostrar null, nunca <Navigate to="/" />.
  const [checkedPath, setCheckedPath] = useState<string | null>(null);
  // redirectTo: destino de una redirección activa encontrada en bk_redirects para la ruta actual.
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Al cambiar de ruta (ej. de la lista de kinesiólogos a un perfil), volver al inicio
  // de la página. Se usa useLayoutEffect (no useEffect) para que el scroll se corrija
  // ANTES de que el navegador pinte la página nueva — con useEffect el usuario alcanza
  // a ver un instante el contenido en la posición de scroll vieja (ej. el footer) antes
  // de saltar arriba, lo que se percibe como un "salto" visible.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const scrollEl = document.getElementById('main_content');
    if (scrollEl) scrollEl.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    // Segundo reset por si el navegador restaura el scroll después del primer render
    // (ej. al volver con el botón "atrás", algunos navegadores reimponen el scroll previo).
    const scrollEl = document.getElementById('main_content');
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
      if (scrollEl) scrollEl.scrollTop = 0;
    }, 100);
    return () => clearTimeout(t);
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (!isKnownRoute(currentPath)) {
      setModuleData(null);   // limpiar datos del alias anterior
      setRedirectTo(null);
      checkRedirect(currentPath);
    } else {
      // Rutas conocidas no necesitan resolver alias; marcar como resuelta.
      setCheckedPath(currentPath);
      setModuleData(null);
      setRedirectTo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isKnownRoute = (path: string): boolean => {
    const knownRoutes = [
      '/',
      '/login',
      '/cuenta',
      '/cuenta/editar',
      '/kinesiologos',
      '/terminos',
      '/quienes-somos',
      '/blog',
      '/banners',
      '/usuarios',
      '/admin-blog',
      '/contactos-whatsapp',
      '/gestion-kinesiologos',
      '/redirecciones',
      '/configuracion',
      '/distintivos',
      '/empresas',
      '/empresas/:company_id',
      '/calificaciones',
      '/preguntas-frecuentes'
    ];

    // Rutas exactas
    if (knownRoutes.includes(path)) {
      return true;
    }

    // Rutas con parámetros válidos
    const validParameterRoutes = [
      { base: '/kinesiologos/', paramRegex: /^\/kinesiologos\/\d+$/ },
      { base: '/blog/', paramRegex: /^\/blog\/[a-zA-Z0-9-]+$/ },
      { base: '/empresas/', paramRegex: /^\/empresas\/\d+$/ }
    ];

    return validParameterRoutes.some(route =>
      path.startsWith(route.base) && route.paramRegex.test(path)
    );
  }

  const checkRedirect = (currentPath: string) => {
    ApiRequest({
      url: "/redirects/check",
      method: "GET",
      query: { path: currentPath },
      setResponse: (response: any) => {
        if (response.status === "success" && response.to_url) {
          setRedirectTo(response.to_url);
          setCheckedPath(currentPath);
        } else {
          getAliasURl(currentPath);
        }
      },
      onError: () => getAliasURl(currentPath),
    });
  }

  const getAliasURl = (currentPath: string) => {
    ApiRequest({
      url: "/alias/getURL",
      method: "GET",
      query: { alias: currentPath },
      setResponse: (response: any) => {
        if (response.status === "success") {
          setModuleData({ module: response.module, id: response.id });
        }
        // Marcar esta URL como resuelta. Si status !== 'success', moduleData queda null → <Navigate to="/" />.
        setCheckedPath(currentPath);
      },
      onError: () => setCheckedPath(currentPath),
    });
  }

  const renderAliasComponent = () => {
    if (!moduleData?.module || !componentMap[moduleData.module]) {
      return <Navigate to="/" />;
    }

    const Component = componentMap[moduleData.module];

    return <Component
      id={moduleData.id}
      setLoading={setLoading}
    />;
  }

  return (
    <>
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Home setLoading={setLoading}/>} />
        <Route path="/login" element={<Login setLoading={setLoading}/>} />


        {role_id === 1 &&
        <Route path="/cuenta" element={<AccountUsers setLoading={setLoading}/>} />
        }

        {role_id === 2 &&
        <Route path="/cuenta" element={<AccountKinesiologists setLoading={setLoading}/>} />
        }

        {role_id === 100 &&
        <Route path="/cuenta" element={<AccountUsers setLoading={setLoading}/>} />
        }


        <Route path="/cuenta/editar" element={<AccountEditor setLoading={setLoading}/>} />
        <Route path="/kinesiologos" element={<KinesiologistsList setLoading={setLoading}/>} />
        <Route path="/kinesiologos/:user_id" element={<KinesiologistDetail setLoading={setLoading}/>} />
        <Route path="/terminos" element={<Terms setLoading={setLoading}/>} />
        <Route path="/quienes-somos" element={<AboutUs/>} />
        <Route path="/blog" element={<GlobalBlog />} />
        <Route path="/blog/:alias" element={<BlogDetails setLoading={setLoading}/>} />
        <Route path="/preguntas-frecuentes" element={<FAQ setLoading={setLoading}/>} />

        {/* Make CompanyDetails route available to all roles */}
        <Route path="/empresas/:company_id" element={<CompanyDetails setLoading={setLoading}/>} />

        {role_id === 100 &&
          <>
            <Route path="/banners" element={<BannersList setLoading={setLoading}/>} />
            <Route path="/usuarios" element={<Users setLoading={setLoading}/>} />
            <Route path="/admin-blog" element={<BlogList setLoading={setLoading}/>} />
            <Route path="/contactos-whatsapp" element={<WhatsappLogsList setLoading={setLoading}/>} />
            <Route path="/gestion-kinesiologos" element={<NursesReport setLoading={setLoading}/>} />
            <Route path="/redirecciones" element={<RedirectsList setLoading={setLoading}/>} />
            <Route path="/configuracion" element={<Settings setLoading={setLoading}/>} />
            <Route path="/distintivos" element={<BadgesList setLoading={setLoading}/>} />
            <Route path="/empresas" element={<CompaniesList setLoading={setLoading}/>} />
            <Route path="/calificaciones" element={<AdminRatingsList setLoading={setLoading}/>} />
          </>
        }

        {/* Ruta para manejar redirecciones (bk_redirects) y alias */}
        <Route path="*" element={
          checkedPath !== location.pathname ? null : (
            redirectTo ? <Navigate to={redirectTo} replace /> :
            moduleData ? renderAliasComponent() : <Navigate to="/" />
          )
        } />
      </Routes>
      </ErrorBoundary>
      <WhatsAppButton />
    </>
  );
}
